import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Guide from "pages/Guide";
import Privacy from "pages/policy/Privacy";
import Service from "pages/policy/Service";
import Benefit from "pages/event/Benefit";
import Comment from "components/molecules/Comment";
import ErrorFallback from "pages/Error";
import { ErrorBoundary } from "react-error-boundary";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import Operation from "pages/policy/Operation";
import Health from "pages/policy/Health";
import Place from "pages/policy/Place";
import StudioPrivacy from "pages/policy/StudioPrivacy";
import PremiumService from "pages/policy/PremiumService";
import PremiumPrivacy from "pages/policy/PremiumPrivacy";
import StudioWeeks21Privacy from "pages/policy/StudioWeeks21Privacy";
import InboundPrivacy from "pages/policy/InboundPrivacy";
import MomsClassPrivacy from "pages/policy/MomsClassPrivacy";
import ServiceEn from "pages/policy/ServiceEn";
import ServiceJa from "pages/policy/ServiceJa";
import PrivacyEn from "pages/policy/PrivacyEn";
import PrivacyJa from "pages/policy/PrivacyJa";
import PrivacyId from "pages/policy/PrivacyId";
import PrivacyEs from "pages/policy/PrivacyEs";
import PrivacyTh from "pages/policy/PrivacyTh";
import PrivacyVi from "pages/policy/PrivacyVi";
import ServiceId from "pages/policy/ServiceId";
import ServiceEs from "pages/policy/ServiceEs";
import ServiceTh from "pages/policy/ServiceTh";
import ServiceVi from "pages/policy/ServiceVi";
import KidsShare from "pages/event/KidsShare";
import { useAuthStore } from "stores/authStore";
import Loading from "components/atoms/Loading";
import RevealReport from "pages/pregnancy/RevealReport";
import RevealReportShare from "pages/pregnancy/RevealReportShare";
import HealthEn from "pages/policy/HealthEn";
import HealthJa from "pages/policy/HealthJa";
import HealthTh from "pages/policy/HealthTh";
import HealthVi from "pages/policy/HealthVi";
import HealthId from "pages/policy/HealthId";
import HealthEs from "pages/policy/HealthEs";
import OperationEn from "pages/policy/OperationEn";
import OperationJa from "pages/policy/OperationJa";
import OperationVi from "pages/policy/OperationVi";
import OperationTh from "pages/policy/OperationTh";
import OperationId from "pages/policy/OperationId";
import OperationEs from "pages/policy/OperationEs";

const withErrorBoundary = (Component: JSX.Element) => (
  <QueryErrorResetBoundary>
    <ErrorBoundary FallbackComponent={ErrorFallback}>{Component}</ErrorBoundary>
  </QueryErrorResetBoundary>
);

// 인증이 필요한 라우트는 AuthOutlet으로 감싸서 토큰이 없는 경우 접근 차단
const AuthOutlet = () => {
  const token = useAuthStore((state) => state.token);
  const isWebView =
    typeof window !== "undefined" && !!window.ReactNativeWebView;
  const waitForToken = isWebView && !token;

  const [isTimeout, setIsTimeout] = useState(false);

  useEffect(() => {
    if (!waitForToken) return;
    const timer = setTimeout(() => setIsTimeout(true), 5000);
    return () => clearTimeout(timer);
  }, [waitForToken]);

  if (isTimeout)
    return <ErrorFallback error={undefined} resetErrorBoundary={() => {}} />;
  if (waitForToken) return <Loading />;
  return <Outlet />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const Router = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Guide />} />
        <Route path="/policy/service" element={<Service />} />
        <Route path="/policy/privacy" element={<Privacy />} />
        <Route path="/policy/operation" element={<Operation />} />
        <Route path="/policy/health" element={<Health />} />
        <Route path="/policy/place" element={<Place />} />
        <Route path="/policy/studioPrivacy" element={<StudioPrivacy />} />
        <Route path="/policy/premiumService" element={<PremiumService />} />
        <Route path="/policy/premiumPrivacy" element={<PremiumPrivacy />} />
        <Route
          path="/policy/studioWeeks21Privacy"
          element={<StudioWeeks21Privacy />}
        />
        <Route path="/policy/inboundPrivacy" element={<InboundPrivacy />} />
        <Route path="/policy/momsClassPrivacy" element={<MomsClassPrivacy />} />
        <Route path="/policy/en/service" element={<ServiceEn />} />
        <Route path="/policy/en/privacy" element={<PrivacyEn />} />
        <Route path="/policy/ja/service" element={<ServiceJa />} />
        <Route path="/policy/ja/privacy" element={<PrivacyJa />} />
        <Route path="/policy/id/privacy" element={<PrivacyId />} />
        <Route path="/policy/es/privacy" element={<PrivacyEs />} />
        <Route path="/policy/th/privacy" element={<PrivacyTh />} />
        <Route path="/policy/vi/privacy" element={<PrivacyVi />} />
        <Route path="/policy/id/service" element={<ServiceId />} />
        <Route path="/policy/es/service" element={<ServiceEs />} />
        <Route path="/policy/th/service" element={<ServiceTh />} />
        <Route path="/policy/vi/service" element={<ServiceVi />} />
        <Route path="/policy/en/health" element={<HealthEn />} />
        <Route path="/policy/ja/health" element={<HealthJa />} />
        <Route path="/policy/th/health" element={<HealthTh />} />
        <Route path="/policy/vi/health" element={<HealthVi />} />
        <Route path="/policy/id/health" element={<HealthId />} />
        <Route path="/policy/es/health" element={<HealthEs />} />
        <Route path="/policy/en/operation" element={<OperationEn />} />
        <Route path="/policy/ja/operation" element={<OperationJa />} />
        <Route path="/policy/vi/operation" element={<OperationVi />} />
        <Route path="/policy/th/operation" element={<OperationTh />} />
        <Route path="/policy/id/operation" element={<OperationId />} />
        <Route path="/policy/es/operation" element={<OperationEs />} />

        <Route
          path="/pregnancy/revealReport/share"
          element={withErrorBoundary(<RevealReportShare />)}
        />

        <Route
          path="/error"
          element={
            <ErrorFallback error={undefined} resetErrorBoundary={() => {}} />
          }
        />

        {/* 토큰이 필요한 라우트 */}
        <Route element={<AuthOutlet />}>
          <Route
            path="/pregnancy/revealReport"
            element={withErrorBoundary(<RevealReport />)}
          />
          <Route
            path="/common/comment"
            element={withErrorBoundary(<Comment svcId={29} />)}
          />
          <Route
            path="/event/benefit"
            element={withErrorBoundary(<Benefit />)}
          />
          <Route
            path="/event/kidsShare"
            element={withErrorBoundary(<KidsShare />)}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};
